/**
 * dashboard-page.tsx — Màn Tổng quan.
 *
 * Bố cục: lời chào → THANH LỌC TOÀN TRANG (kênh · tài khoản · kỳ) → 6 thẻ KPI
 * → khối báo cáo "Hiệu quả Chat → Chốt".
 *
 * Vì sao bộ lọc nằm ở đây chứ không nằm trong khối báo cáo: trước đây kỳ và
 * phạm vi là trạng thái riêng của <ChatOrderReport>, còn sáu thẻ KPI gọi API
 * không tham số nên luôn hiện "hôm nay / 7 ngày" của TOÀN công ty. Người dùng
 * chọn tháng trước, hoặc chọn một tài khoản Zalo, thì phần trên vẫn đứng yên —
 * cùng một màn hình mà hai mốc thời gian, hai phạm vi. Nay chỉ có MỘT chỗ giữ
 * `mode` + `scope`, truyền xuống cả hai khối, và backend giải kỳ bằng chung
 * một hàm.
 *
 * Ba thẻ ghi "hiện tại" cố ý KHÔNG theo kỳ lọc: chưa trả lời, chưa đọc, tổng
 * khách hàng là ảnh chụp trạng thái lúc xem. Bảng dữ liệu chỉ lưu trạng thái
 * hiện thời, không lưu lịch sử trạng thái, nên "tháng trước còn bao nhiêu hội
 * thoại chưa trả lời" là câu hỏi không có đáp án đúng — hiện số hôm nay mà gắn
 * nhãn tháng trước thì còn sai hơn.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarClock, MailOpen, MessageSquare, MessageSquareWarning, RefreshCw, UserPlus, Users,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { Loading } from '@/components/shared/feedback'
import { useAuthStore } from '@/stores/auth-store'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useDashboardKpi, type ChatReportMode } from '@/hooks/use-chat-report'
import { ALL_SCOPE, ChatOrderReport, PeriodFilter, ScopeFilter } from './chat-order-report'
import { TraceDialog } from './trace-dialog'
import type { ChiSoTruyVet } from '@/hooks/use-drilldown'

export function DashboardPage() {
  const { t } = useTranslation('reports')
  const isMobile = useIsMobile()
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()

  const [mode, setMode] = useState<ChatReportMode>({ kind: 'period', period: 'month' })
  const [pham, setPham] = useState({ channel: ALL_SCOPE, accountId: ALL_SCOPE })
  const scope = {
    channel: pham.channel === ALL_SCOPE ? undefined : pham.channel,
    accountId: pham.accountId === ALL_SCOPE ? undefined : pham.accountId,
  }

  const { data, isLoading } = useDashboardKpi(mode, scope)

  // Một hộp thoại truy vết dùng chung cho cả trang — mọi thẻ số đều mở nó.
  const [truyVet, setTruyVet] = useState<ChiSoTruyVet | null>(null)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['dashboard-kpi'] })
    qc.invalidateQueries({ queryKey: ['chat-report'] })
    qc.invalidateQueries({ queryKey: ['dashboard-message-volume'] })
    toast.success(t('dashboard.refreshed'))
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-start justify-between gap-3">
        <PageHeader
          title={t('dashboard.greeting', { name: user?.fullName || '' })}
          // Điện thoại: bỏ đoạn mô tả dài — tiêu đề + bộ lọc là đủ.
          description={isMobile ? undefined : t('dashboard.description')}
        />
        <Button variant="outline" size="sm" onClick={refresh} aria-label={t('dashboard.refreshAria')}>
          <RefreshCw className="h-4 w-4" />
          <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
        </Button>
      </div>

      {/* ★ Thanh lọc toàn trang — ghim trên cùng để luôn thấy đang xem kỳ nào */}
      <div className="sticky top-0 z-20 -mx-4 flex items-center gap-2 overflow-x-auto border-b bg-background/95 px-4 py-2.5 backdrop-blur sm:-mx-6 sm:flex-wrap sm:overflow-visible sm:px-6 sm:py-3 [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <ScopeFilter channel={pham.channel} accountId={pham.accountId} onChange={setPham} />
        <PeriodFilter mode={mode} onChange={setMode} />
        <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
          {t('dashboard.currentPeriod')} <span className="font-semibold text-foreground">{data?.periodLabel ?? '…'}</span>
        </span>
      </div>

      {isLoading || !data ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-3">
          <StatCard
            label={t('dashboard.stats.messages')}
            onClick={() => setTruyVet('messages')}
            value={data.messagesInRange}
            icon={MessageSquare}
            hint={data.periodLabel}
          />
          <StatCard
            label={t('dashboard.stats.unrepliedConversations')}
            onClick={() => setTruyVet('unrepliedConversations')}
            value={data.unrepliedConversations}
            icon={MessageSquareWarning}
            tone="warning"
            hint={t('dashboard.hints.needsAction')}
          />
          <StatCard
            label={t('dashboard.stats.unreadMessages')}
            onClick={() => setTruyVet('unreadMessages')}
            value={data.unreadMessages}
            icon={MailOpen}
            hint={t('dashboard.hints.unreadIn', { count: data.unreadConversations })}
          />
          <StatCard
            label={t('dashboard.stats.appointments')}
            onClick={() => setTruyVet('appointments')}
            value={data.appointmentsInRange}
            icon={CalendarClock}
            tone="success"
            hint={data.periodLabel}
          />
          <StatCard
            label={t('dashboard.stats.newContacts')}
            onClick={() => setTruyVet('newContacts')}
            value={data.newContactsInRange}
            icon={UserPlus}
            hint={data.periodLabel}
          />
          <StatCard
            label={t('dashboard.stats.totalContacts')}
            onClick={() => setTruyVet('totalContacts')}
            value={data.totalContacts}
            icon={Users}
            hint={t('dashboard.hints.active')}
          />
        </div>
      )}

      {/* ★ Khối báo cáo chính: Hiệu quả Chat → Chốt */}
      <ChatOrderReport mode={mode} scope={scope} onTruyVet={setTruyVet} />

      <TraceDialog metric={truyVet} mode={mode} scope={scope} onClose={() => setTruyVet(null)} />
    </div>
  )
}
